/**
 * Copy for a From sender the account can no longer send as (ENG-3916).
 *
 * Two reasons, each naming the sender and the email it belongs to, because a
 * scheduled interview can carry several senders and "pick another sender" is
 * useless without saying which one. The same builder feeds both the inline
 * field error (where the sender object is on hand, so the name is real) and
 * the save-time toast (where only the error code is, so the name degrades).
 *
 * Mailbox-not-connected keeps its own wording in `errorCopy.ts` — it is a
 * connection problem rather than a permission one.
 */

export type SenderErrorReason = 'deactivated' | 'delegation';

/** The email a sender belongs to, as it reads inside the sentence. */
export const SENDER_USE_CASE = {
  candidateConfirmation: 'candidate confirmation email',
  interviewerConfirmation: 'interviewer confirmation email',
  slotSelected: 'slot selected email',
} as const;

export type SenderUseCase =
  (typeof SENDER_USE_CASE)[keyof typeof SENDER_USE_CASE];

/** Stands in when the sender's name can't be resolved (toast path). */
const UNNAMED_SENDER = 'This sender';

export interface SenderErrorMessageArgs {
  reason: SenderErrorReason;
  useCase: SenderUseCase;
  /** Sender's display name; omitted where only the error code is available. */
  userName?: string | null;
}

export function getSenderErrorMessage({
  reason,
  useCase,
  userName,
}: SenderErrorMessageArgs): string {
  const subject = userName?.trim() || UNNAMED_SENDER;
  const cause =
    reason === 'deactivated'
      ? `${subject} is deactivated in Kula.`
      : `${subject} hasn't allowed sending emails on their behalf.`;
  return `${cause} Select a different sender for the ${useCase}`;
}

/**
 * Backend codes → the reason + email they describe. Per-block by design: the
 * shared `err_email_delegation_disabled` it replaces couldn't say which email
 * the sender belonged to.
 */
export const SENDER_ERROR_CODES: Record<
  string,
  { reason: SenderErrorReason; useCase: SenderUseCase }
> = {
  err_candidate_email_sender_deactivated: {
    reason: 'deactivated',
    useCase: SENDER_USE_CASE.candidateConfirmation,
  },
  err_candidate_email_sender_delegation_disabled: {
    reason: 'delegation',
    useCase: SENDER_USE_CASE.candidateConfirmation,
  },
  err_interviewer_email_sender_deactivated: {
    reason: 'deactivated',
    useCase: SENDER_USE_CASE.interviewerConfirmation,
  },
  err_interviewer_email_sender_delegation_disabled: {
    reason: 'delegation',
    useCase: SENDER_USE_CASE.interviewerConfirmation,
  },
  // `candidate_mail_from` on AtsSchedule — the slot-selection email in the
  // self-schedule flow.
  err_candidate_mail_sender_deactivated: {
    reason: 'deactivated',
    useCase: SENDER_USE_CASE.slotSelected,
  },
  err_candidate_mail_sender_delegation_disabled: {
    reason: 'delegation',
    useCase: SENDER_USE_CASE.slotSelected,
  },
};
